/**
 * @param {object}  props
 * @param {string=} props.className  Extra Tailwind classes for the `<header>`.
 * @returns {JSX.Element}
 */

import React, { useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { getAuth, signOut } from "firebase/auth";
import { firebaseApp } from "../../firebase/firebaseApp";
import { HomeIcon } from "../icons/HomeIcon";
import { LogoutIcon } from "../icons/LogoutIcon";
import { useTranslation } from "react-i18next";
import LangSwitcher from "../LangSwitcher";
import { useAuth } from "../../context/AuthContext";
import { AdminIcon } from "../icons/AdminIcon";

/* ------------------------------------------------------------------ */
/* Component                                                          */
/* ------------------------------------------------------------------ */
function NavBar({ className = "" }) {
  const { t } = useTranslation();
  const { user, claims } = useAuth(); // grab host’s user

  const role = claims?.role;
  // Use toLowerCase for a case-insensitive check
  const isAdmin =
    ["clinician", "admin"].includes(role) ||
    user?.email?.toLowerCase() === "ben@benwjd.com";

  const navigate = useNavigate();

  /** Sign user out and redirect to `/auth/login` */
  const handleSignOut = useCallback(() => {
    signOut(getAuth(firebaseApp))
      .then(() => navigate("/auth/login", { replace: true }))
      .catch(
        (err) => process.env.NODE_ENV === "development" && console.error(err)
      );
  }, [navigate]);

  return (
    <>
      {/* ── Top bar ─────────────────────────────────────────────────── */}
      <header
        className={`flex items-center justify-between w-full p-2 md:gap-4 z-50 ${className}`}
      >
        {/* Logo */}
        <Link to="/home" aria-label="PDX Home">
          <img
            src="/pdx_logo.png"
            alt="PDX logo"
            className="h-12 w-auto"
            loading="lazy"
          />
        </Link>

        <nav className="flex items-center gap-4">
          <LangSwitcher />
          <Link to="/home" aria-label="Home">
            <HomeIcon />
          </Link>
          {isAdmin && (
            <Link to="/admin" aria-label="Admin">
              <AdminIcon />
            </Link>
          )}
          <button
            type="button"
            onClick={handleSignOut}
            aria-label="Sign out"
            className="focus:outline-none"
          >
            <LogoutIcon />
          </button>
        </nav>
      </header>
    </>
  );
}

export default React.memo(NavBar);
